// 字体调试工具
class FontDebugger {
  constructor() {
    this.debugInfo = {
      fontFiles: [],
      fontFaceRules: [],
      loadedFonts: [],
      errors: []
    };
  }

  // 检查字体文件是否存在
  async checkFontFiles() {
    // 使用正确的webpack别名路径
    const fontFiles = [
      '@/assets/fonts/AlibabaPuHuiTi-3-55-Regular/AlibabaPuHuiTi-3-55-Regular.woff2',
      '@/assets/fonts/AlibabaPuHuiTi-3-55-Regular/AlibabaPuHuiTi-3-55-Regular.woff',
      '@/assets/fonts/AlibabaPuHuiTi-3-55-Regular/AlibabaPuHuiTi-3-55-Regular.ttf',
      '@/assets/fonts/AlibabaPuHuiTi-3-75-SemiBold/AlibabaPuHuiTi-3-75-SemiBold.woff2',
      '@/assets/fonts/AlibabaPuHuiTi-3-75-SemiBold/AlibabaPuHuiTi-3-75-SemiBold.woff',
      '@/assets/fonts/AlibabaPuHuiTi-3-75-SemiBold/AlibabaPuHuiTi-3-75-SemiBold.ttf'
    ];

    for (const fontFile of fontFiles) {
      try {
        // 尝试使用webpack别名路径
        const response = await fetch(fontFile);
        this.debugInfo.fontFiles.push({
          file: fontFile,
          exists: response.ok,
          status: response.status,
          statusText: response.statusText
        });
      } catch (error) {
        // 如果webpack别名路径失败，尝试相对路径
        try {
          const relativePath = fontFile.replace('@/', '');
          const response = await fetch(relativePath);
          this.debugInfo.fontFiles.push({
            file: fontFile,
            exists: response.ok,
            status: response.status,
            statusText: response.statusText,
            note: '使用相对路径'
          });
        } catch (relativeError) {
          this.debugInfo.fontFiles.push({
            file: fontFile,
            exists: false,
            error: `webpack别名和相对路径都失败: ${error.message}, ${relativeError.message}`
          });
        }
      }
    }
  }

  // 检查CSS中的@font-face规则
  checkFontFaceRules() {
    const styleSheets = Array.from(document.styleSheets);
    
    styleSheets.forEach((sheet, index) => {
      try {
        const rules = Array.from(sheet.cssRules || sheet.rules);
        rules.forEach(rule => {
          if (rule instanceof CSSFontFaceRule) {
            this.debugInfo.fontFaceRules.push({
              fontFamily: rule.style.getPropertyValue('font-family'),
              src: rule.style.getPropertyValue('src'),
              fontWeight: rule.style.getPropertyValue('font-weight'),
              fontStyle: rule.style.getPropertyValue('font-style')
            });
          }
        });
      } catch (error) {
        // 跨域样式表可能无法访问
        console.warn(`无法访问样式表 ${index}:`, error);
      }
    });
  }

  // 检查已加载的字体
  async checkLoadedFonts() {
    if (document.fonts && document.fonts.check) {
      const testFonts = [
        'Alibaba PuHuiTi',
        'Arial',
        'Helvetica'
      ];

      for (const font of testFonts) {
        const isLoaded = document.fonts.check(`12px "${font}"`);
        this.debugInfo.loadedFonts.push({
          font: font,
          loaded: isLoaded
        });
      }
    }
  }

  // 测试字体渲染
  testFontRendering() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const testString = '测试文字Alibaba';
    
    // 测试阿里普惠字体
    ctx.font = '16px "Alibaba PuHuiTi"';
    const alibabaWidth = ctx.measureText(testString).width;
    
    // 测试备用字体
    ctx.font = '16px Arial';
    const arialWidth = ctx.measureText(testString).width;
    
    // 测试系统字体
    ctx.font = '16px -apple-system';
    const systemWidth = ctx.measureText(testString).width;
    
    return {
      alibabaWidth,
      arialWidth,
      systemWidth,
      alibabaLoaded: alibabaWidth !== arialWidth,
      systemLoaded: systemWidth !== arialWidth
    };
  }

  // 检查网络请求
  async checkNetworkRequests() {
    const performanceEntries = performance.getEntriesByType('resource');
    const fontRequests = performanceEntries.filter(entry => 
      entry.name.includes('font') || 
      entry.name.includes('Alibaba') ||
      entry.name.includes('.woff') ||
      entry.name.includes('.ttf')
    );
    
    return fontRequests.map(request => ({
      name: request.name,
      duration: request.duration,
      size: request.transferSize,
      status: request.responseStatus
    }));
  }

  // 生成调试报告
  async generateDebugReport() {
    console.log('=== 字体调试报告 ===');
    
    await this.checkFontFiles();
    this.checkFontFaceRules();
    await this.checkLoadedFonts();
    
    const renderingTest = this.testFontRendering();
    const networkRequests = await this.checkNetworkRequests();
    
    console.log('1. 字体文件检查:');
    this.debugInfo.fontFiles.forEach(file => {
      console.log(`   ${file.file}: ${file.exists ? '✅ 存在' : '❌ 不存在'} ${file.error ? `(${file.error})` : ''} ${file.note ? `[${file.note}]` : ''}`);
    });
    
    console.log('\n2. @font-face 规则:');
    this.debugInfo.fontFaceRules.forEach(rule => {
      console.log(`   字体族: ${rule.fontFamily}`);
      console.log(`   字重: ${rule.fontWeight}`);
      console.log(`   样式: ${rule.fontStyle}`);
      console.log(`   源: ${rule.src}`);
    });
    
    console.log('\n3. 已加载字体:');
    this.debugInfo.loadedFonts.forEach(font => {
      console.log(`   ${font.font}: ${font.loaded ? '✅ 已加载' : '❌ 未加载'}`);
    });
    
    console.log('\n4. 字体渲染测试:');
    console.log(`   阿里普惠字体宽度: ${renderingTest.alibabaWidth}`);
    console.log(`   Arial字体宽度: ${renderingTest.arialWidth}`);
    console.log(`   系统字体宽度: ${renderingTest.systemWidth}`);
    console.log(`   阿里普惠字体加载: ${renderingTest.alibabaLoaded ? '✅ 成功' : '❌ 失败'}`);
    
    console.log('\n5. 网络请求:');
    networkRequests.forEach(request => {
      console.log(`   ${request.name}: ${request.duration}ms, ${request.size}bytes`);
    });
    
    return {
      fontFiles: this.debugInfo.fontFiles,
      fontFaceRules: this.debugInfo.fontFaceRules,
      loadedFonts: this.debugInfo.loadedFonts,
      renderingTest,
      networkRequests
    };
  }

  // 修复建议
  getFixSuggestions(report) {
    const suggestions = [];
    
    // 检查字体文件是否存在
    const missingFiles = report.fontFiles.filter(file => !file.exists);
    if (missingFiles.length > 0) {
      suggestions.push('字体文件路径问题，建议检查文件路径和webpack配置');
      suggestions.push('尝试使用webpack别名路径 ~@/assets/fonts/');
    }
    
    // 检查@font-face规则
    if (report.fontFaceRules.length === 0) {
      suggestions.push('未找到@font-face规则，检查CSS文件是否正确加载');
    }
    
    // 检查字体加载状态
    const alibabaFont = report.loadedFonts.find(font => font.font === 'Alibaba PuHuiTi');
    if (alibabaFont && !alibabaFont.loaded) {
      suggestions.push('阿里普惠字体未加载，检查字体文件路径和格式');
    }
    
    // 检查渲染测试
    if (!report.renderingTest.alibabaLoaded) {
      suggestions.push('字体渲染测试失败，字体可能未正确加载');
    }
    
    return suggestions;
  }
}

// 导出实例
export const fontDebugger = new FontDebugger();

// 默认导出
export default FontDebugger; 